import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { CycleTypeMultiselect, AccountValueMultiselect, MeetingDispositionMultiselect, TeamDropdown, SalesRepDropdown } from './index';

interface FilterBarProps {
  // Filter values
  selectedCycleTypes: string[];
  selectedAccountValues: string[];
  selectedMeetingDispositions: string[];
  selectedTeam: string | null;
  selectedSalesRep: string | null;

  // Filter change handlers
  onCycleTypesChange: (values: string[]) => void;
  onAccountValuesChange: (values: string[]) => void;
  onMeetingDispositionsChange: (values: string[]) => void;
  onTeamChange: (value: string | null) => void;
  onSalesRepChange: (value: string | null) => void;

  // Optional props
  isFetching?: boolean;
  showClearAll?: boolean;
  className?: string;
  layout?: 'horizontal' | 'vertical' | 'grid';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({
  selectedCycleTypes,
  selectedAccountValues,
  selectedMeetingDispositions,
  selectedTeam,
  selectedSalesRep,
  onCycleTypesChange,
  onAccountValuesChange,
  onMeetingDispositionsChange,
  onTeamChange,
  onSalesRepChange,
  isFetching = false,
  showClearAll = true,
  className = "",
  layout = 'grid',
  collapsible = false,
  defaultCollapsed = true
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const activeFiltersCount = [
    selectedCycleTypes.length > 0,
    selectedAccountValues.length > 0,
    selectedMeetingDispositions.length > 0,
    selectedTeam && selectedTeam !== '',
    selectedSalesRep && selectedSalesRep !== ''
  ].filter(Boolean).length;

  const handleClearAll = () => {
    onCycleTypesChange([]);
    onAccountValuesChange([]);
    // Empty array = "All Meeting Dispositions" (same pattern as AccountValue)
    onMeetingDispositionsChange([]);
    onTeamChange(null);
    onSalesRepChange(null);
  };

  const getLayoutClasses = () => {
    switch (layout) {
      case 'horizontal':
        return 'flex space-x-4';
      case 'vertical':
        return 'space-y-4';
      case 'grid':
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4';
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm ${className}`}>
      {/* Header with filter count, clear all, and collapse toggle */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 font-inter">Filters</h3>
          {activeFiltersCount > 0 && (
            <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-3 py-1 rounded-full text-sm font-medium font-inter">
              {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {activeFiltersCount > 0 && showClearAll && (
            <button
              onClick={handleClearAll}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 text-sm font-medium font-inter transition-colors"
            >
              Clear all
            </button>
          )}
          
          {collapsible && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm font-medium font-inter transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>{isCollapsed ? 'Show filters' : 'Hide filters'}</span>
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Filter components - collapsible */}
      {(!collapsible || !isCollapsed) && (
        <div className="px-4 pb-4">
          <div className={getLayoutClasses()}>
            {/* Cycle Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-inter">
                Cycle Type
              </label>
              <CycleTypeMultiselect
                selectedValues={selectedCycleTypes}
                onChange={onCycleTypesChange}
                isFetching={isFetching}
              />
            </div>

            {/* Account Value Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-inter">
                Account Value
              </label>
              <AccountValueMultiselect
                selectedValues={selectedAccountValues}
                onChange={onAccountValuesChange}
                isFetching={isFetching}
              />
            </div>

            {/* Meeting Disposition Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-inter">
                Meeting Disposition
              </label>
              <MeetingDispositionMultiselect
                selectedValues={selectedMeetingDispositions}
                onChange={onMeetingDispositionsChange}
                isFetching={isFetching}
              />
            </div>

            {/* Team Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-inter">
                Team
              </label>
              <TeamDropdown 
                selectedValue={selectedTeam}
                onChange={onTeamChange}
                isFetching={isFetching}
              />
            </div>

            {/* Sales Rep Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-inter">
                Sales Rep
              </label>
              <SalesRepDropdown 
                selectedValue={selectedSalesRep}
                onChange={onSalesRepChange}
                selectedTeam={selectedTeam}
                isFetching={isFetching}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
